import { getMessaging, getToken } from 'firebase/messaging';

import { sentryLogger } from '@/adapters/logger/sentryLogger';
import { initializeFirebaseWeb } from '@/adapters/pushNotification/firebaseInitialization.web';
import type { PushNotificationAdapter } from '@/adapters/pushNotification/types';

const createFirebaseMessagingAdapter = (): PushNotificationAdapter => {
  const vapidKey = process.env.EXPO_PUBLIC_FIREBASE_VAPID_KEY;

  const state: { messaging: ReturnType<typeof getMessaging> | null } = { messaging: null };

  const getFirebaseMessaging = () => {
    if (!state.messaging) {
      state.messaging = getMessaging(initializeFirebaseWeb());
    }
    return state.messaging;
  };

  const fetchToken = async (): Promise<string> => {
    if (!vapidKey || Notification.permission !== 'granted') {
      return '';
    }
    return getToken(getFirebaseMessaging(), { vapidKey });
  };

  return {
    getToken: fetchToken,

    requestPermission: async () => {
      if (!('Notification' in window)) {
        sentryLogger.captureMessage('Push notifications not supported in this browser', 'info');
        return false;
      }

      if (Notification.permission === 'granted') {
        return true;
      }

      if (Notification.permission === 'denied') {
        return false;
      }

      const permission = await Notification.requestPermission();
      return permission === 'granted';
    },

    getPermissionStatus: async () => {
      if (!('Notification' in window)) {
        return 'denied';
      }

      return Notification.permission === 'default' ? 'undetermined' : Notification.permission;
    },

    onTokenRefresh: (callback) => {
      if (!vapidKey) {
        sentryLogger.captureMessage(
          'Firebase VAPID key not configured for web push notifications',
          'warning',
        );
        return () => {};
      }

      const tokenState = { lastToken: '' };

      const checkToken = async () => {
        const token = await fetchToken();
        if (token && token !== tokenState.lastToken) {
          tokenState.lastToken = token;
          callback(token);
        }
      };

      void checkToken();

      const intervalId = setInterval(() => void checkToken(), 60 * 60 * 1000);

      return () => clearInterval(intervalId);
    },
  };
};

const firebaseMessagingAdapter = createFirebaseMessagingAdapter();

export { firebaseMessagingAdapter };
